"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Steps,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import type { Deployment, Service } from "../domain/models";
import { HealthTag, PageHeader, serviceName } from "../components/common";
import { roles, services } from "../mocks/data";
import { deliveryApi, type DeliveryActor } from "../services/deliveryApi";
import { useOpsStore } from "../stores/useOpsStore";

interface ReleaseFormValues {
  serviceId: string;
  version: string;
  strategy: Deployment["strategy"];
  batchSize: number;
  intervalSeconds: number;
  canaryPercent?: number;
  approvalNo: string;
  changeNote: string;
  confirmProduction: boolean;
}

const makeIdempotencyKey = () =>
  globalThis.crypto?.randomUUID?.() ?? `release-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const nextPatchVersion = (version: string) => {
  const match = version.match(/^v(\d+)\.(\d+)\.(\d+)/);
  return match ? `v${match[1]}.${match[2]}.${Number(match[3]) + 1}` : "v1.0.0";
};

const statusMeta = {
  success: { color: "green", label: "成功" },
  running: { color: "blue", label: "执行中" },
  failed: { color: "red", label: "失败" },
  rolled_back: { color: "orange", label: "已回滚" },
} as const;

export function ReleasesPage() {
  const { message } = App.useApp();
  const currentUser = useOpsStore((state) => state.currentUser);
  const [form] = Form.useForm<ReleaseFormValues>();
  const [tab, setTab] = useState("records");
  const [records, setRecords] = useState<Deployment[]>([]);
  const [serviceRows, setServiceRows] = useState<Service[]>(() => services.map((item) => ({ ...item })));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [reviewValues, setReviewValues] = useState<ReleaseFormValues>();
  const [publishing, setPublishing] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");

  const strategy = Form.useWatch("strategy", form);
  const selectedServiceId = Form.useWatch("serviceId", form) ?? reviewValues?.serviceId;
  const selectedService = serviceRows.find((item) => item.id === selectedServiceId);
  const role = roles.find((item) => item.id === currentUser?.roleId);
  const canRelease = currentUser?.roleId === "role-admin" || currentUser?.roleId === "role-ops-admin";
  const actor: DeliveryActor | undefined = currentUser && role ? {
    name: currentUser.displayName,
    roleId: currentUser.roleId as DeliveryActor["roleId"],
    roleName: role.name,
  } : undefined;

  const loadRecords = async () => setRecords(await deliveryApi.listDeployments());

  useEffect(() => {
    let cancelled = false;
    void deliveryApi.listDeployments().then((nextRecords) => {
      if (!cancelled) setRecords(nextRecords);
    });
    return () => { cancelled = true; };
  }, []);

  const openRelease = () => {
    if (!canRelease) return;
    const service = serviceRows[0];
    setWorkflowStep(0);
    setReviewValues(undefined);
    setIdempotencyKey(makeIdempotencyKey());
    form.setFieldsValue({
      serviceId: service.id,
      version: nextPatchVersion(service.version),
      strategy: "滚动发布",
      batchSize: 1,
      intervalSeconds: 30,
      canaryPercent: 10,
      approvalNo: `CHG-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}-`,
      changeNote: "",
      confirmProduction: false,
    });
    setDrawerOpen(true);
  };

  const changeService = (serviceId: string) => {
    const service = serviceRows.find((item) => item.id === serviceId);
    if (!service) return;
    form.setFieldsValue({
      serviceId,
      version: nextPatchVersion(service.version),
      batchSize: Math.min(2, Math.max(1, service.instanceCount)),
      confirmProduction: false,
    });
    setReviewValues(undefined);
  };

  const prepareReview = async () => {
    const values = await form.validateFields();
    setReviewValues(values);
    setWorkflowStep(1);
  };

  const createRelease = async () => {
    if (!actor || !reviewValues) return;
    setPublishing(true);
    try {
      const result = await deliveryApi.createRelease({
        ...reviewValues,
        environment: "production",
        idempotencyKey,
        actor,
      });
      await loadRecords();
      setServiceRows(services.map((item) => ({ ...item })));
      setTab("records");
      setDrawerOpen(false);
      message.success(`${serviceName(result.deployment.serviceId, services)} ${result.deployment.version} 发布成功，审计记录已生成`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : "创建发布失败");
    } finally {
      setPublishing(false);
    }
  };

  const releaseColumns = useMemo(() => [
    { title: "发布单", dataIndex: "id", width: 118 },
    { title: "服务", dataIndex: "serviceId", render: (value: string) => serviceName(value, services) },
    { title: "目标版本", dataIndex: "version", render: (value: string) => <Tag color="blue">{value}</Tag> },
    { title: "策略", dataIndex: "strategy" },
    {
      title: "状态",
      dataIndex: "status",
      render: (value: Deployment["status"]) => <Tag color={statusMeta[value].color}>{statusMeta[value].label}</Tag>,
    },
    { title: "操作人", dataIndex: "operator" },
    { title: "时间", dataIndex: "createdAt", width: 170 },
    { title: "变更说明", dataIndex: "changeNote" },
  ], []);

  return <>
    <PageHeader
      title="版本与发布"
      description="追踪服务版本、发布策略与回滚记录"
      extra={
        <Tooltip title={canRelease ? undefined : "当前角色无服务发布权限"}>
          <Button type="primary" icon={<PlayCircleOutlined />} disabled={!canRelease} onClick={openRelease}>
            创建发布
          </Button>
        </Tooltip>
      }
    />
    <Alert
      showIcon
      type="info"
      message="Mock 发布通道"
      description="当前流程模拟审批、发布和审计写入，不会调用 Kubernetes、Argo Rollouts 或真实生产环境。"
      className="page-alert"
    />
    <Card variant="borderless">
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "records",
            label: "发布记录",
            children: <Table
              rowKey="id"
              dataSource={records}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              columns={releaseColumns}
            />,
          },
          {
            key: "versions",
            label: "当前版本",
            children: <Table
              rowKey="id"
              dataSource={serviceRows}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              columns={[
                { title: "服务", dataIndex: "name" },
                { title: "服务编码", dataIndex: "code" },
                { title: "运行版本", dataIndex: "version", render: (value) => <Tag>{value}</Tag> },
                { title: "状态", dataIndex: "status", render: (value) => <HealthTag status={value} /> },
                { title: "实例", render: (_, record) => `${record.healthyInstances}/${record.instanceCount}` },
                { title: "负责人", dataIndex: "owner" },
              ]}
            />,
          },
        ]}
      />
    </Card>

    <Drawer
      className="workflow-drawer"
      title="创建发布"
      width={720}
      open={drawerOpen}
      destroyOnHidden
      maskClosable={!publishing}
      closable={!publishing}
      onClose={() => setDrawerOpen(false)}
      footer={<div className="workflow-footer">
        {workflowStep === 1 && <Button icon={<ArrowLeftOutlined />} disabled={publishing} onClick={() => setWorkflowStep(0)}>返回修改</Button>}
        <div className="filter-spacer" />
        <Button disabled={publishing} onClick={() => setDrawerOpen(false)}>取消</Button>
        {workflowStep === 0
          ? <Button type="primary" onClick={() => void prepareReview()}>下一步：确认发布</Button>
          : <Button type="primary" danger icon={<PlayCircleOutlined />} loading={publishing} onClick={() => void createRelease()}>确认发布</Button>}
      </div>}
    >
      <Steps
        size="small"
        current={workflowStep}
        items={[{ title: "发布参数" }, { title: "风险确认" }]}
        className="workflow-steps"
      />
      {workflowStep === 0 ? <Form form={form} layout="vertical" requiredMark="optional">
        <Alert
          type="warning"
          showIcon
          message="生产发布高风险操作"
          description="请核对目标服务、版本和发布策略。真实接入后还需服务端权限校验、审批状态校验与执行平台幂等保护。"
          className="page-alert"
        />
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="目标服务" name="serviceId" rules={[{ required: true, message: "请选择目标服务" }]}>
              <Select
                showSearch
                optionFilterProp="label"
                onChange={changeService}
                options={serviceRows.map((item) => ({ value: item.id, label: item.name }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="环境">
              <Input value="生产环境" disabled />
            </Form.Item>
          </Col>
        </Row>
        {selectedService && selectedService.status !== "healthy" && <Alert
          type={selectedService.status === "critical" || selectedService.status === "offline" ? "error" : "warning"}
          showIcon
          icon={<ExclamationCircleOutlined />}
          message={`当前服务状态：${selectedService.status === "warning" ? "警告" : selectedService.status === "critical" ? "异常" : "离线"}`}
          description={`健康实例 ${selectedService.healthyInstances}/${selectedService.instanceCount}。发布前应确认故障是否与本次变更相关。`}
          className="page-alert"
        />}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="当前版本">
              <Input value={selectedService?.version} disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="目标版本"
              name="version"
              rules={[
                { required: true, message: "请输入目标版本" },
                { pattern: /^v\d+\.\d+\.\d+(?:[-.][a-z0-9]+)*$/i, message: "格式：v1.2.3" },
              ]}
            >
              <Input placeholder="v1.2.3" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="发布策略" name="strategy" rules={[{ required: true }]}>
          <Select options={["滚动发布", "金丝雀发布", "蓝绿发布"].map((value) => ({ value, label: value }))} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="单批实例数" name="batchSize" rules={[{ required: true }]}>
              <InputNumber min={1} max={Math.max(1, selectedService?.instanceCount ?? 50)} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="批次间隔（秒）" name="intervalSeconds" rules={[{ required: true }]}>
              <InputNumber min={10} max={600} step={10} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="金丝雀流量"
              name="canaryPercent"
              rules={strategy === "金丝雀发布" ? [{ required: true, message: "请输入流量比例" }] : []}
            >
              <InputNumber min={5} max={50} precision={0} addonAfter="%" disabled={strategy !== "金丝雀发布"} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="变更单号"
          name="approvalNo"
          rules={[
            { required: true, message: "请输入变更单号" },
            { pattern: /^CHG-\d{8}-\d{3,6}$/i, message: "格式：CHG-YYYYMMDD-序号" },
          ]}
        >
          <Input placeholder="CHG-20260725-001" />
        </Form.Item>
        <Form.Item label="发布说明" name="changeNote" rules={[{ required: true, min: 4, message: "请填写至少 4 个字的发布说明" }]}>
          <Input.TextArea rows={3} maxLength={200} showCount placeholder="说明发布内容、影响范围、验证方式和回滚条件" />
        </Form.Item>
        <Form.Item
          name="confirmProduction"
          valuePropName="checked"
          rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error("请确认生产环境风险")) }]}
        >
          <Checkbox>我已核对目标服务和发布参数，并确认当前仅执行 Mock 发布</Checkbox>
        </Form.Item>
      </Form> : <div>
        <Alert
          type="warning"
          showIcon
          message={`即将发布 ${selectedService?.name} ${reviewValues?.version}`}
          description={`当前版本 ${selectedService?.version}，发布策略 ${reviewValues?.strategy}。本次操作将新增发布记录并写入审计日志。`}
          className="page-alert"
        />
        <Descriptions bordered size="small" column={2} className="review-descriptions">
          <Descriptions.Item label="目标环境"><Tag color="red">生产</Tag></Descriptions.Item>
          <Descriptions.Item label="操作人">{currentUser?.displayName}</Descriptions.Item>
          <Descriptions.Item label="目标服务">{selectedService?.name}</Descriptions.Item>
          <Descriptions.Item label="版本变化">{selectedService?.version} → {reviewValues?.version}</Descriptions.Item>
          <Descriptions.Item label="发布策略">{reviewValues?.strategy}</Descriptions.Item>
          <Descriptions.Item label="批次参数">{reviewValues?.batchSize} 个/批，间隔 {reviewValues?.intervalSeconds} 秒</Descriptions.Item>
          {reviewValues?.strategy === "金丝雀发布" && <Descriptions.Item label="金丝雀流量">{reviewValues.canaryPercent}%</Descriptions.Item>}
          <Descriptions.Item label="变更单号" span={2}>{reviewValues?.approvalNo}</Descriptions.Item>
          <Descriptions.Item label="发布说明" span={2}>{reviewValues?.changeNote}</Descriptions.Item>
        </Descriptions>
        <div className="release-impact-grid">
          <div><span>服务实例</span><b>{selectedService?.instanceCount ?? 0}</b></div>
          <div><span>健康实例</span><b className={selectedService?.healthyInstances === selectedService?.instanceCount ? "success-text" : "warning-text"}>{selectedService?.healthyInstances ?? 0}</b></div>
          <div><span>当前可用率</span><b>{selectedService?.availability ?? 0}%</b></div>
          <div><span>执行模式</span><b>Mock</b></div>
        </div>
        <div className="production-confirm">
          <CheckCircleOutlined />
          <div><b>二次确认已完成</b><span>重复提交由幂等键保护；真实发布适配器当前处于关闭状态。</span></div>
        </div>
      </div>}
    </Drawer>
  </>;
}

export default ReleasesPage;
